import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Collapse, 
  IconButton, 
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  OutlinedInput,
  Autocomplete,
  Popper,
  Grow,
  InputAdornment,
  SelectChangeEvent,
  RadioGroup,
  Radio,
  FormControlLabel,
  debounce,
  Tooltip
} from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CancelIcon from '@mui/icons-material/Cancel';
import ClearIcon from '@mui/icons-material/Clear';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoIcon from '@mui/icons-material/Info';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axiosInstance from '../services/axiosInstance';
import FolderIcon from '@mui/icons-material/Folder';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageIcon from '@mui/icons-material/Image';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { sv } from 'date-fns/locale';

// Constants
const FILE_TYPES = [
  { value: 'image', label: 'Bilder' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Ljud' },
  { value: 'document', label: 'Dokument' },
  { value: 'other', label: 'Övrigt' }
];

const EMPTY_RECENT_SEARCHES: string[] = [];

// Types for search parameters
export interface AdvancedSearchParams {
  searchTerm: string;
  fileTypes: string[];
  fromDate: Date | null;
  toDate: Date | null;
  favorite: boolean | null;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  searchFields: string[];
  recentSearches?: string[];
  searchOperator?: 'AND' | 'OR';
  collectionId?: string | null;
}

interface AdvancedSearchBarProps {
  onSearch: (params: AdvancedSearchParams) => void;
  initialParams?: Partial<AdvancedSearchParams>;
  recentSearches?: string[];
  onSaveSearch?: (params: AdvancedSearchParams) => void;
  onAddCategoryFilter?: (categoryId: string) => void;
  onAddTagFilter?: (tag: string, kind: 'tag' | 'aiTag') => void;
  onResetAll?: () => void;
  filtersOnly?: boolean;
}

const initialSearchParams: AdvancedSearchParams = {
  searchTerm: '',
  fileTypes: [],
  fromDate: null,
  toDate: null,
  favorite: null,
  sortBy: 'uploadedAt',
  sortDirection: 'desc',
  searchFields: ['title', 'description', 'tags', 'aiTags', 'aiDescription', 'fileName', 'metadata'],
  recentSearches: [],
  searchOperator: 'OR'
};

const buildSearchParams = (
  initialParams: Partial<AdvancedSearchParams>,
  recentSearches: string[]
): AdvancedSearchParams => {
  const mergedParams: AdvancedSearchParams = {
    ...initialSearchParams,
    ...initialParams,
    recentSearches
  };
  const searchFields = mergedParams.searchFields || initialSearchParams.searchFields;
  return {
    ...mergedParams,
    searchFields: searchFields.includes('metadata') ? searchFields : [...searchFields, 'metadata']
  };
};

const AdvancedSearchBar: React.FC<AdvancedSearchBarProps> = ({ 
  onSearch, 
  initialParams = {}, 
  recentSearches = EMPTY_RECENT_SEARCHES,
  onSaveSearch,
  onAddCategoryFilter,
  onAddTagFilter,
  onResetAll,
  filtersOnly = false
}) => {
  // Merge initial params with defaults
  const [params, setParams] = useState<AdvancedSearchParams>(buildSearchParams(initialParams, recentSearches));
  
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(filtersOnly);
  type Suggestion = { kind: 'recent' | 'saved' | 'fileType' | 'category' | 'tag' | 'aiTag'; value: string; id?: string };
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [savedSearches, setSavedSearches] = useState<{ name: string; params: AdvancedSearchParams }[]>(() => {
    try {
      const raw = localStorage.getItem('savedSearches');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionRequestIdRef = useRef(0);
  const isEditingSearchRef = useRef(false);

  const invalidateSuggestionRequests = () => {
    suggestionRequestIdRef.current += 1;
  };

  const shouldPreserveFocusedSearchTerm = (prevSearchTerm: string, nextSearchTerm: string) => {
    if (prevSearchTerm === nextSearchTerm) return false;
    return isEditingSearchRef.current;
  };
 
  useEffect(() => {
    const nextParams = buildSearchParams(initialParams, recentSearches);
    setParams(prev => {
      const mergedParams = shouldPreserveFocusedSearchTerm(prev.searchTerm, nextParams.searchTerm)
        ? { ...nextParams, searchTerm: prev.searchTerm }
        : nextParams;
      return JSON.stringify(prev) === JSON.stringify(mergedParams) ? prev : mergedParams;
    });
  }, [initialParams, recentSearches]);

  useEffect(() => {
    if (filtersOnly) {
      setShowAdvancedSearch(true);
    }
  }, [filtersOnly]);
  
  // Compute applied filters to show as chips
  useEffect(() => {
    const filters = [];
    
    if (params.fileTypes.length > 0) {
      filters.push(`Filtyp: ${params.fileTypes.map(type => 
        FILE_TYPES.find(ft => ft.value === type)?.label || type
      ).join(', ')}`);
    }
    
    if (params.fromDate || params.toDate) {
      let dateFilter = 'Datum: ';
      if (params.fromDate) 
        dateFilter += params.fromDate.toLocaleDateString('sv-SE');
      if (params.fromDate && params.toDate) 
        dateFilter += ' - ';
      if (params.toDate) 
        dateFilter += params.toDate.toLocaleDateString('sv-SE');
      filters.push(dateFilter);
    }
    
    if (params.favorite !== null) {
      filters.push(`Favoriter: ${params.favorite ? 'Ja' : 'Nej'}`);
    }
    
    setAppliedFilters(filters);
  }, [params]);
  
  // Create a debounced search function that will be called as the user types
  const debouncedSearch = useCallback(
    debounce((searchParams: AdvancedSearchParams) => {
      onSearch(searchParams);
    }, 500),
    [onSearch]
  );

  // Handle main search input change with dynamic searching
  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    isEditingSearchRef.current = true;
    const value = e.target.value;
    const trimmed = value.trim();
    const updatedParams = { ...params, searchTerm: value };
    setParams(updatedParams);
    
    // Update search suggestions
    if (trimmed.length > 2) {
      // Trigger search immediately instead of waiting for suggestions to return.
      debouncedSearch(updatedParams);

      const requestId = suggestionRequestIdRef.current + 1;
      suggestionRequestIdRef.current = requestId;
      const needle = trimmed.toLowerCase();
      const recents = (params.recentSearches || [])
        .filter(s => s.toLowerCase().includes(needle))
        .slice(0, 5)
        .map(s => ({ kind: 'recent' as const, value: s }));

      const saved = savedSearches
        .filter(s => s.name.toLowerCase().includes(needle))
        .slice(0, 5)
        .map(s => ({ kind: 'saved' as const, value: s.name }));

      const fileTypeHits = FILE_TYPES
        .filter(ft => ft.label.toLowerCase().includes(needle))
        .slice(0, 5)
        .map(ft => ({ kind: 'fileType' as const, value: ft.value }));

      // Fetch backend suggestions for categories/tags/aiTags
      try {
        const resp = await axiosInstance.get(`/api/assets/suggest`, { params: { q: value } });
        if (requestId !== suggestionRequestIdRef.current) return;
        if (resp.status === 200) {
          const data = resp.data;
          const categorySugs: Suggestion[] = (data.categories || []).map((c: any) => ({ kind: 'category', value: c.name, id: c.id }));
          const tagSugs: Suggestion[] = (data.tags || []).map((t: string) => ({ kind: 'tag', value: t }));
          const aiTagSugs: Suggestion[] = (data.aiTags || []).map((t: string) => ({ kind: 'aiTag', value: t }));
          setSuggestions([...recents, ...saved, ...fileTypeHits, ...categorySugs, ...tagSugs, ...aiTagSugs].slice(0, 12));
        } else {
          setSuggestions([...recents, ...saved, ...fileTypeHits].slice(0, 8));
        }
      } catch {
        if (requestId !== suggestionRequestIdRef.current) return;
        setSuggestions([...recents, ...saved, ...fileTypeHits].slice(0, 8));
      }
    } else {
      invalidateSuggestionRequests();
      setSuggestions([]);
      // If field becomes empty, trigger a search to reset results without requiring Enter
      if (trimmed.length === 0) {
        debouncedSearch(updatedParams);
      }
    }
  };

  // Clear search input field
  const handleClearSearch = () => {
    invalidateSuggestionRequests();
    setParams({ ...params, searchTerm: '' });
    setSuggestions([]);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Trigger search with empty term
    debouncedSearch({ ...params, searchTerm: '' });
  };

  const handleSuggestionClick = (s: Suggestion) => {
    invalidateSuggestionRequests();
    if (s.kind === 'recent') {
      const updated = { ...params, searchTerm: s.value };
      setParams(updated);
      setSuggestions([]);
      onSearch(updated);
    } else if (s.kind === 'saved') {
      const match = savedSearches.find(x => x.name === s.value);
      if (match) applySavedSearch(match);
      setSuggestions([]);
    } else if (s.kind === 'category') {
      if (onAddCategoryFilter && s.id) {
        onAddCategoryFilter(s.id);
        // Clear free text when promoting to category filter
        const updatedClear = { ...params, searchTerm: '' };
        setParams(updatedClear);
        onSearch(updatedClear);
      } else {
        // fallback – sök på namn
        const updated = { ...params, searchTerm: s.value };
        setParams(updated);
        onSearch(updated);
      }
      setSuggestions([]);
    } else if (s.kind === 'tag' || s.kind === 'aiTag') {
      if (onAddTagFilter) {
        onAddTagFilter(s.value, s.kind);
        // Clear free text after promoting selection to a structured filter
        setParams(prev => ({ ...prev, searchTerm: '' }));
        setSuggestions([]);
      } else {
        const updated = { ...params, searchTerm: `${params.searchTerm} ${s.value}`.trim() };
        setParams(updated);
        setSuggestions([]);
        onSearch(updated);
      }
    } else if (s.kind === 'fileType') {
      if (!params.fileTypes.includes(s.value)) {
        const updated = { ...params, fileTypes: [...params.fileTypes, s.value] };
        setParams(updated);
        setSuggestions([]);
        debouncedSearch(updated);
      }
    }
  };

  // Handle pressing enter in search field  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };
  
  // Submit the search
  const handleSearchSubmit = () => {
    invalidateSuggestionRequests();
    onSearch(params);
    setSuggestions([]); // Clear suggestions after search
  };
  
  // Toggle advanced search panel
  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const renderSearchOperatorToggle = () => (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={params.searchOperator}
      onChange={(_, value) => {
        if (!value) return;
        const updated = { ...params, searchOperator: value } as AdvancedSearchParams;
        setParams(updated);
        onSearch(updated);
      }}
      aria-label="Matcha villkor"
    >
      <Tooltip title="Matcha alla sökord">
        <ToggleButton value="AND" aria-label="matcha alla">
          AND
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Matcha något sökord">
        <ToggleButton value="OR" aria-label="matcha något">
          OR
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );

  // Save current search locally (only params from search bar; App filters hanteras i App)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const handleSaveSearch = () => setSaveDialogOpen(true);
  const confirmSaveSearch = () => {
    const name = (saveName || '').trim();
    if (!name) { setSaveDialogOpen(false); setSaveName(''); return; }
    const entry = { name, params };
    const updated = [entry, ...savedSearches.filter(s => s.name !== name)].slice(0, 20);
    setSavedSearches(updated);
    localStorage.setItem('savedSearches', JSON.stringify(updated));
    setSaveDialogOpen(false);
    setSaveName('');
  };

  const applySavedSearch = (entry: { name: string; params: AdvancedSearchParams }) => {
    setParams(entry.params);
    onSearch(entry.params);
  };
  
  // Clear all filters and search term
  const handleClearAll = () => {
    invalidateSuggestionRequests();
    setParams(initialSearchParams);
    setSuggestions([]);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Trigger search with cleared params
    onSearch(initialSearchParams);
  };
  
  // Handle removing a filter chip
  const handleRemoveFilter = (filterText: string) => {
    // Extract filter type from text
    if (filterText.startsWith('Filtyp:')) {
      setParams({ ...params, fileTypes: [] });
    } else if (filterText.startsWith('Datum:')) {
      setParams({ ...params, fromDate: null, toDate: null });
    } else if (filterText.startsWith('Favoriter:')) {
      setParams({ ...params, favorite: null });
    }
  };
  
  // Parse negative (excluded) terms from the search text
  const parseExcludeTerms = (text: string): string[] => {
    if (!text) return [];
    return text.trim().split(/\s+/)
      .filter(t => /^-[a-zA-ZåäöÅÄÖéèêëàáâãæç0-9]/i.test(t))
      .map(t => t.substring(1));
  };

  const excludeTerms = parseExcludeTerms(params.searchTerm);

  // Remove a single excluded term from the search text
  const handleRemoveExcludeTerm = (term: string) => {
    const regex = new RegExp(`\\s*-${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
    const newSearchTerm = params.searchTerm.replace(regex, '').trim();
    const updatedParams = { ...params, searchTerm: newSearchTerm };
    setParams(updatedParams);
    debouncedSearch(updatedParams);
  };

  // Handle updating file type selection
  const handleFileTypeChange = (event: SelectChangeEvent<string[]>) => {
    const updatedParams = { ...params, fileTypes: event.target.value as string[] };
    setParams(updatedParams);
    debouncedSearch(updatedParams);
  };

  const advancedPanelOpen = filtersOnly || showAdvancedSearch;

  return (
    <Box sx={{ width: '100%' }} className="advanced-search-bar">
      {!filtersOnly && (
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mb: 2, 
          alignItems: 'center' 
        }}>
          {onResetAll && (
            <Tooltip title="Rensa sökfält och kategorier">
              <IconButton
                onClick={onResetAll}
                size="small"
                sx={{ color: '#555', '&:hover': { color: '#072f53' } }}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          )}

          <TextField
            placeholder="Sök efter bilder, dokument... (använd -ord för att utesluta)"
            variant="outlined"
            size="small"
            value={params.searchTerm}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            onFocus={() => { isEditingSearchRef.current = true; }}
            onBlur={() => {
              isEditingSearchRef.current = false;
              setTimeout(() => setSuggestions([]), 120);
            }}
            onKeyDown={(e) => { if (e.key === 'Escape') setSuggestions([]); }}
            fullWidth
            inputRef={searchInputRef}
            sx={{
              bgcolor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: params.searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    aria-label="clear search"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          
          <Button 
            variant="contained" 
            onClick={handleSearchSubmit}
            disabled={!params.searchTerm && appliedFilters.length === 0}
          >
            Sök
          </Button>
          
          <Tooltip title="Avancerad sökning">
            <IconButton 
              color={showAdvancedSearch ? "primary" : "default"}
              onClick={toggleAdvancedSearch}
              aria-label="Avancerad sökning"
            >
              <TuneIcon />
            </IconButton>
          </Tooltip>

          {renderSearchOperatorToggle()}
        </Box>
      )}
      
      {/* Autocomplete suggestions */}
      {!filtersOnly && suggestions.length > 0 && (
        <ClickAwayListener onClickAway={() => setSuggestions([])}>
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              zIndex: 1000,
              width: searchInputRef.current?.offsetWidth || 'auto',
              mt: -2,
              maxHeight: '200px',
              overflowY: 'auto',
              bgcolor: 'white'
            }}
          >
            {suggestions.map((suggestion, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {suggestion.kind === 'category' && <FolderIcon fontSize="small" color="action" />}
                  {suggestion.kind === 'tag' && <LocalOfferIcon fontSize="small" color="action" />}
                  {suggestion.kind === 'aiTag' && <AutoAwesomeIcon fontSize="small" color="action" />}
                  {suggestion.kind === 'fileType' && <ImageIcon fontSize="small" color="action" />}
                  <Typography variant="body2">
                    {suggestion.kind === 'fileType' ? `Filtyp: ${FILE_TYPES.find(ft => ft.value === suggestion.value)?.label || suggestion.value}`
                      : suggestion.kind === 'category' ? `Kategori: ${suggestion.value}`
                      : suggestion.kind === 'tag' ? `Tagg: ${suggestion.value}`
                      : suggestion.kind === 'aiTag' ? `AI‑tagg: ${suggestion.value}`
                      : suggestion.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        </ClickAwayListener>
      )}
      
      {/* Applied filters + exclude chips */}
      {(appliedFilters.length > 0 || excludeTerms.length > 0) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 2 }}>
          {appliedFilters.map((filter, index) => (
            <Chip
              key={index}
              label={filter}
              onDelete={() => handleRemoveFilter(filter)}
              color="primary"
              variant="outlined"
              size="small"
            />
          ))}
          {excludeTerms.map((term) => (
            <Chip
              key={`exclude-${term}`}
              label={`Utesluter: ${term}`}
              onDelete={() => handleRemoveExcludeTerm(term)}
              color="error"
              variant="outlined"
              size="small"
            />
          ))}
          {appliedFilters.length > 0 && (
            <Chip
              label="Rensa alla"
              onClick={handleClearAll}
              color="secondary"
              variant="outlined"
              size="small"
              icon={<CancelIcon />}
            />
          )}
        </Box>
      )}
      
      {/* Advanced search panel */}
      <Collapse in={advancedPanelOpen}>
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
          {filtersOnly ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="subtitle1">
                Avancerade filter
              </Typography>
              {renderSearchOperatorToggle()}
            </Box>
          ) : (
            <Typography variant="subtitle1" gutterBottom>
              Avancerad sökning
            </Typography>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* File type filter */}
            <FormControl size="small">
              <InputLabel id="file-type-label">Filtyp</InputLabel>
              <Select
                labelId="file-type-label"
                multiple
                value={params.fileTypes}
                onChange={handleFileTypeChange}
                input={<OutlinedInput label="Filtyp" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip 
                        key={value} 
                        label={FILE_TYPES.find(type => type.value === value)?.label || value} 
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
              >
                {FILE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Checkbox checked={params.fileTypes.indexOf(type.value) > -1} />
                    <ListItemText primary={type.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Date range picker */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
                <DatePicker
                  label="Från datum"
                  value={params.fromDate}
                  onChange={(date: Date | null) => {
                    const updatedParams = { ...params, fromDate: date };
                    setParams(updatedParams);
                    debouncedSearch(updatedParams);
                  }}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                  label="Till datum"
                  value={params.toDate}
                  onChange={(date: Date | null) => {
                    const updatedParams = { ...params, toDate: date };
                    setParams(updatedParams);
                    debouncedSearch(updatedParams);
                  }}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
              {/* Quick ranges */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button size="small" onClick={() => {
                  const from = new Date();
                  from.setDate(from.getDate() - 7);
                  const updated = { ...params, fromDate: from, toDate: new Date() };
                  setParams(updated);
                  debouncedSearch(updated);
                }}>7 dagar</Button>
                <Button size="small" onClick={() => {
                  const from = new Date();
                  from.setDate(from.getDate() - 30);
                  const updated = { ...params, fromDate: from, toDate: new Date() };
                  setParams(updated);
                  debouncedSearch(updated);
                }}>30 dagar</Button>
                <Button size="small" onClick={() => {
                  const from = new Date();
                  from.setMonth(from.getMonth() - 3);
                  const updated = { ...params, fromDate: from, toDate: new Date() };
                  setParams(updated);
                  debouncedSearch(updated);
                }}>90 dagar</Button>
              </Box>
            </Box>
            
            {/* Favorite filter */}
            <FormControl size="small">
              <InputLabel id="favorite-label">Favoriter</InputLabel>
              <Select
                labelId="favorite-label"
                value={params.favorite === null ? '' as any : params.favorite ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value;
                  const updatedParams = { 
                    ...params, 
                    favorite: value === '' ? null : value === 'true'
                  };
                  setParams(updatedParams);
                  debouncedSearch(updatedParams);
                }}
                label="Favoriter"
              >
                <MenuItem value=""><em>Alla</em></MenuItem>
                <MenuItem value="true">Endast favoriter</MenuItem>
                <MenuItem value="false">Uteslut favoriter</MenuItem>
              </Select>
            </FormControl>
            
            {/* Sort options */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="sort-by-label">Sortera efter</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={params.sortBy}
                  onChange={(e) => {
                    const updatedParams = { ...params, sortBy: e.target.value };
                    setParams(updatedParams);
                    debouncedSearch(updatedParams);
                  }}
                  label="Sortera efter"
                >
                  <MenuItem value="uploadedAt">Uppladdningsdatum</MenuItem>
                  <MenuItem value="modifiedAt">Ändringsdatum</MenuItem>
                  <MenuItem value="fileName">Filnamn</MenuItem>
                  <MenuItem value="fileSize">Filstorlek</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="sort-direction-label">Riktning</InputLabel>
                <Select
                  labelId="sort-direction-label"
                  value={params.sortDirection}
                  onChange={(e) => {
                    const updatedParams = { 
                      ...params, 
                      sortDirection: e.target.value as 'asc' | 'desc'
                    };
                    setParams(updatedParams);
                    debouncedSearch(updatedParams);
                  }}
                  label="Riktning"
                >
                  <MenuItem value="desc">Fallande</MenuItem>
                  <MenuItem value="asc">Stigande</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Search fields */}
            <FormControl size="small">
              <InputLabel id="search-fields-label">Sök i fält</InputLabel>
              <Select
                labelId="search-fields-label"
                multiple
                value={params.searchFields}
                onChange={(e) => {
                  const updatedParams = { 
                    ...params, 
                    searchFields: e.target.value as string[]
                  };
                  setParams(updatedParams);
                  debouncedSearch(updatedParams);
                }}
                input={<OutlinedInput label="Sök i fält" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip 
                        key={value} 
                        label={
                          value === 'title' ? 'Titel' : 
                          value === 'description' ? 'Beskrivning' : 
                          value === 'tags' ? 'Taggar' : 
                          value === 'aiTags' ? 'AI-taggar' : 
                          value === 'aiDescription' ? 'AI-beskrivning' : 
                          value === 'fileName' ? 'Filnamn' : 
                          value === 'metadata' ? 'Templatefält (metadata.*)' : 
                          value
                        } 
                        size="small" 
                      />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="title">
                  <Checkbox checked={params.searchFields.includes('title')} />
                  <ListItemText primary="Titel" />
                </MenuItem>
                <MenuItem value="description">
                  <Checkbox checked={params.searchFields.includes('description')} />
                  <ListItemText primary="Beskrivning" />
                </MenuItem>
                <MenuItem value="tags">
                  <Checkbox checked={params.searchFields.includes('tags')} />
                  <ListItemText primary="Taggar" />
                </MenuItem>
                <MenuItem value="aiTags">
                  <Checkbox checked={params.searchFields.includes('aiTags')} />
                  <ListItemText primary="AI-taggar" />
                </MenuItem>
                <MenuItem value="aiDescription">
                  <Checkbox checked={params.searchFields.includes('aiDescription')} />
                  <ListItemText primary="AI-beskrivning" />
                </MenuItem>
                <MenuItem value="fileName">
                  <Checkbox checked={params.searchFields.includes('fileName')} />
                  <ListItemText primary="Filnamn" />
                </MenuItem>
                <MenuItem value="metadata">
                  <Checkbox checked={params.searchFields.includes('metadata')} />
                  <ListItemText primary="Templatefält (metadata.*)" />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleClearAll} 
              sx={{ mr: 1 }}
            >
              Rensa alla
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSearchSubmit}
            >
              Sök
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Save search (only visible when advanced panel is open) */}
      {showAdvancedSearch && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="outlined" size="small" onClick={handleSaveSearch}>Spara sökning</Button>
        </Box>
      )}

      {/* Save dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Spara sökning</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Namn på sökningen"
            type="text"
            fullWidth
            variant="outlined"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Avbryt</Button>
          <Button variant="contained" onClick={confirmSaveSearch}>Spara</Button>
        </DialogActions>
      </Dialog>

      {/* Saved searches list (basic) */}
      {savedSearches.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {savedSearches.map((s, idx) => (
            <Chip
              key={idx}
              label={s.name}
              onClick={() => applySavedSearch(s)}
              onDelete={() => {
                const updated = savedSearches.filter((_, i) => i !== idx);
                setSavedSearches(updated);
                localStorage.setItem('savedSearches', JSON.stringify(updated));
              }}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AdvancedSearchBar; 